<template>
  <iColumn
    gap="large"
    width="fill"
    vertical-align="middle"
  >
    <!-- <iRow>
      <iText>
        This setting determines the date format that will be used across the publication's date elememts,
        unless the format is specified on the element itself.
      </iText>
    </iRow> -->
    <iRow>
      <iSelect
        :items="dateFormatOptions"
        :model-value="selectedSetting"
        value-field="value"
        text-field="label"
        @update:model-value="selectSetting"
      />
    </iRow>
    <iRow v-if="selectedSetting?.label==='Custom'">
      <iTextInput v-model="customFormatString" width="fill" />
    </iRow>
    <iColumn vertical-align="middle">
      <iTitle>
        Preview
      </iTitle>
      <iText variant="subtle">
        {{ formattedDatePreview }}
      </iText>
    </iColumn>
    <iRow>
      <iButton size="small" :disabled="!isDirty" @click="save">
        Save Settings
      </iButton>
    </iRow>
  </iColumn>
</template>

<script>
import { useConfigurationStore } from "%/stores/configuration-store";
import { mapActions, mapState } from "pinia";
import { timeAgo } from "~/helpers/publication-helper";
import dayjs from "dayjs";
import DateFormat from "%/dynamic-components/render-engine/options/date-format";

export default {
  name: "DateFormatSettings",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close"],
  data() {
    return {
      // Seeded to the first preset so the first render before `created()`
      // doesn't deref `null.label` inside the iSelect's slot rendering —
      // the cloned Studio path mounts this component synchronously and
      // any null read crashed the whole config panel.
      selectedSetting: { label: "", value: "" },
      oldSetting: null,
      customFormatString: "MMMM D, YYYY",
    };
  },
  computed: {
    presetOptions() {
      return DateFormat.DATE_FORMAT_OPTIONS;
    },
    dateFormatOptions() {
      const customValue = this.customFormatString || this.dateFormat?.value || "";
      return [
        ...this.presetOptions,
        {
          label: "Custom",
          value: customValue,
          secondaryLabel: this.getFormattedSample(customValue),
        },
      ];
    },
    selectedDateFormat() {
      if (this.selectedSetting?.label === "Custom") {
        return this.customFormatString;
      }
      return this.selectedSetting?.value ?? "";
    },
    // True when the user changed the selection vs. the current saved
    // configuration. Wrapping the comparison here keeps the template
    // free of `dateFormat?.value` derefs that crashed when the store
    // wasn't hydrated (Studio destinations).
    isDirty() {
      return this.selectedDateFormat !== (this.dateFormat?.value ?? "");
    },
    ...mapState(useConfigurationStore, [
      "dateFormat",
    ]),
    formattedDatePreview() {
      let timestamp = Date.now() / 1000;
      if (this.selectedDateFormat === "timeAgo") {
        return `${this.timeAgo(timestamp)}`;
      } else {
        return this.dayjs(timestamp).format(this.selectedDateFormat);
      }
    },
  },
  watch: {
    "dateFormat.value": {
      handler(value) {
        if (value !== undefined && value !== null) {
          this.customFormatString = value;
        }
      },
      immediate: true,
    },
  },
  created() {
    this.oldSetting = this.dateFormat;
    // Studio destinations don't hydrate `useConfigurationStore` on every
    // page render, so `dateFormat` can be undefined when the user
    // navigates straight to /branding/theme/date-format. Fall back to
    // the first preset rather than dereferencing undefined and crashing
    // the editor.
    const currentValue = this.dateFormat?.value ?? this.presetOptions[0]?.value ?? "";
    const matchingOption = this.presetOptions.find(option => option.value === currentValue);
    if (matchingOption) {
      this.selectedSetting = matchingOption;
    } else {
      this.selectedSetting = {
        label: "Custom",
        value: currentValue,
      };
      this.customFormatString = currentValue;
    }
  },
  methods: {
    dayjs,
    timeAgo,
    ...mapActions(useConfigurationStore, ["updateConfiguration", "logDateFormatChange"]),
    closeModal() {
      this.$emit("close");
    },
    selectSetting(setting) {
      this.selectedSetting = setting;
      if (setting.label === "Custom") {
        this.customFormatString = setting.value;
      }
    },
    save() {
      if (this.selectedSetting) {
        this.selectedSetting.value = this.selectedDateFormat;
        this.updateConfiguration({
          dateFormat: this.selectedSetting,
        });
        this.closeModal();
      } else {
        this.closeModal();
      }
    },
    cancel() {
      this.closeModal();
    },
    getFormattedSample(formatString) {
      if (!formatString) {
        return "";
      }

      if (formatString === "timeAgo") {
        return `${this.timeAgo(Date.now() / 1000)}`;
      }
      return this.dayjs().format(formatString);
    },
  },
};
</script>
